import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'

export interface LegalInternalRecoveriesRow {
  month: string
  values: Record<string, number>
  total: number
}

export interface LegalInternalRecoverySelection {
  month: string
  source?: string
}

@Component({
  selector: 'app-legal-internal-recoveries-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './legal-internal-recoveries-table.component.html',
  styleUrl: './legal-internal-recoveries-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegalInternalRecoveriesTableComponent {
  @Input({ required: true }) rows: LegalInternalRecoveriesRow[] = []
  @Input({ required: true }) dataSources: string[] = []
  @Input() loading = false
  @Input() selectedCell: LegalInternalRecoverySelection | null = null
  @Output() cellSelected = new EventEmitter<LegalInternalRecoverySelection>()

  private readonly currencyFormatter = new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2,
  })
  private readonly monthFormatter = new Intl.DateTimeFormat('en-GB', {
    month: 'long', year: 'numeric',
  })

  formatMonth(value: string): string {
    const match = value.match(/^(\d{4})-(\d{2})$/)
    return match
      ? this.monthFormatter.format(new Date(Number(match[1]), Number(match[2]) - 1, 1))
      : value
  }

  formatAmount(value: number | undefined): string {
    return this.currencyFormatter.format(value ?? 0)
  }

  totalFor(source: string): number {
    return this.rows.reduce((total, row) => total + (row.values[source] ?? 0), 0)
  }

  grandTotal(): number {
    return this.rows.reduce((total, row) => total + row.total, 0)
  }

  selectCell(month: string, source?: string): void {
    this.cellSelected.emit({ month, source })
  }

  isSelectedCell(month: string, source?: string): boolean {
    return this.selectedCell?.month === month && this.selectedCell.source === source
  }
}
